import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { TrendingUp, TrendingDown, Minus, Info } from "lucide-react";

interface DualPriceDisplayProps {
  unitPrice: number | null;
  casePrice: number | null;
  comparisonUnitPrice?: number | null;
  comparisonCasePrice?: number | null;
  rawTerms?: {
    price_basis_text?: string;
    derivedMethod?: string;
  };
  label: string;
  className?: string;
}

export function DualPriceDisplay({
  unitPrice,
  casePrice,
  comparisonUnitPrice,
  comparisonCasePrice,
  rawTerms,
  label,
  className
}: DualPriceDisplayProps) {
  const formatPrice = (price: number | null) => {
    if (price === null) return 'N/A';
    return `$${price.toFixed(2)}`;
  };

  const calculateVariance = (price: number | null, comparisonPrice: number | null) => {
    if (!price || !comparisonPrice) return null;
    
    const variance = price - comparisonPrice;
    const percentage = (variance / comparisonPrice) * 100;
    
    return {
      absolute: variance,
      percentage,
      direction: variance > 0 ? 'higher' : variance < 0 ? 'lower' : 'equal'
    };
  };

  const unitVariance = calculateVariance(unitPrice, comparisonUnitPrice);
  const caseVariance = calculateVariance(casePrice, comparisonCasePrice);

  const getVarianceIcon = (direction: string) => {
    switch (direction) {
      case 'higher':
        return <TrendingUp className="w-3 h-3" />;
      case 'lower':
        return <TrendingDown className="w-3 h-3" />;
      default:
        return <Minus className="w-3 h-3" />;
    }
  };

  const getVarianceColor = (direction: string) => {
    switch (direction) {
      case 'higher':
        return 'text-red-600';
      case 'lower':
        return 'text-green-600';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{label}</span>
        {rawTerms?.price_basis_text && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-3 h-3 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <div className="space-y-1">
                  <p className="font-medium">Source Information</p>
                  <p className="text-xs">Raw text: "{rawTerms.price_basis_text}"</p>
                  {rawTerms.derivedMethod && (
                    <p className="text-xs text-muted-foreground">
                      Method: {rawTerms.derivedMethod}
                    </p>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      
      <div className="space-y-1">
        {/* Unit Price Row */}
        <div className="flex items-center justify-between">
          <span className="text-sm">
            {formatPrice(unitPrice)} / unit
          </span>
          {unitVariance && (
            <div className={`flex items-center space-x-1 text-xs ${getVarianceColor(unitVariance.direction)}`}>
              {getVarianceIcon(unitVariance.direction)}
              <span>
                {unitVariance.absolute > 0 ? '+' : ''}
                {formatPrice(unitVariance.absolute)} ({unitVariance.percentage.toFixed(1)}%)
              </span>
            </div>
          )}
        </div>
        
        {/* Case Price Row */}
        <div className="flex items-center justify-between">
          <span className="text-sm">
            {formatPrice(casePrice)} / case
          </span>
          {caseVariance && (
            <div className={`flex items-center space-x-1 text-xs ${getVarianceColor(caseVariance.direction)}`}>
              {getVarianceIcon(caseVariance.direction)}
              <span>
                {caseVariance.absolute > 0 ? '+' : ''}
                {formatPrice(caseVariance.absolute)} ({caseVariance.percentage.toFixed(1)}%)
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}